/**
 * Parser CSV mínimo e robusto (client-side) — Item 391-C.
 *
 * `papaparse` não está nas deps do monorepo; o caso de uso (importar a base do
 * tenant) não justifica adicionar uma dependência. Este parser cobre o que uma
 * base exportada de planilha/ERP produz:
 *   - separador vírgula;
 *   - campos entre aspas duplas (que podem conter vírgula e quebra de linha);
 *   - aspas escapadas por duplicação (`""` → `"`) dentro de campo citado;
 *   - 1ª linha = cabeçalho (nomes das colunas);
 *   - CRLF / LF / CR como fim de linha;
 *   - linhas em branco ignoradas.
 *
 * Saída: `{ headers, rows }` onde cada `row` é um objeto coluna→valor (string).
 * Colunas além do cabeçalho são ignoradas; colunas faltantes viram `''`.
 */

export interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
}

/** Tokeniza o texto bruto em uma matriz de células, respeitando aspas. */
function tokenize(text: string): string[][] {
  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let inQuotes = false;
  let fieldStarted = false;

  const pushField = (): void => {
    record.push(field);
    field = '';
    fieldStarted = false;
  };
  const pushRecord = (): void => {
    pushField();
    records.push(record);
    record = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1; // pula a aspa escapada
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"' && !fieldStarted) {
      inQuotes = true;
      fieldStarted = true;
      continue;
    }
    if (ch === ',') {
      pushField();
      continue;
    }
    if (ch === '\r') {
      // CRLF: o \n seguinte é consumido pela checagem abaixo.
      if (text[i + 1] === '\n') i += 1;
      pushRecord();
      continue;
    }
    if (ch === '\n') {
      pushRecord();
      continue;
    }
    field += ch;
    fieldStarted = true;
  }

  // Último campo/registro (arquivo sem newline final).
  if (field.length > 0 || record.length > 0 || fieldStarted) {
    pushRecord();
  }

  return records;
}

/** Uma célula vazia/whitespace-only é considerada ausente. */
function isBlankRecord(cells: string[]): boolean {
  return cells.every((c) => c.trim().length === 0);
}

/**
 * Parseia um texto CSV completo. Lança `Error` legível quando não há cabeçalho
 * ou nenhuma linha de dado.
 */
export function parseCsv(text: string): ParsedCsv {
  const records = tokenize(text).filter((cells) => !isBlankRecord(cells));
  if (records.length === 0) {
    throw new Error('Arquivo vazio: nenhuma linha encontrada.');
  }

  const headers = records[0]!.map((h) => h.trim());
  if (headers.every((h) => h.length === 0)) {
    throw new Error('Cabeçalho ausente: a primeira linha deve conter os nomes das colunas.');
  }

  const dataRecords = records.slice(1);
  if (dataRecords.length === 0) {
    throw new Error('Nenhuma linha de dado após o cabeçalho.');
  }

  const rows: Record<string, string>[] = dataRecords.map((cells) => {
    const row: Record<string, string> = {};
    headers.forEach((header, idx) => {
      if (header.length === 0) return; // ignora coluna sem nome
      row[header] = (cells[idx] ?? '').trim();
    });
    return row;
  });

  return { headers: headers.filter((h) => h.length > 0), rows };
}

/**
 * Sugere automaticamente qual coluna mapeia para cada campo, por aproximação do
 * nome do cabeçalho. Retorna o nome da coluna ou `undefined` se nada casar.
 */
const FIELD_HINTS: Record<keyof ImportFieldHints, string[]> = {
  phone: ['telefone', 'celular', 'phone', 'whatsapp', 'fone', 'msisdn', 'numero', 'número'],
  holderName: ['titular', 'nome', 'cliente', 'name', 'holder', 'razao', 'razão'],
  cpfCnpj: ['cpf', 'cnpj', 'documento', 'doc'],
  contractNumber: ['contrato', 'contract', 'plano', 'assinatura'],
  role: ['papel', 'role', 'funcao', 'função', 'tipo'],
};

export interface ImportFieldHints {
  phone?: string;
  holderName?: string;
  cpfCnpj?: string;
  contractNumber?: string;
  role?: string;
}

function normalizeHeader(h: string): string {
  return h
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // remove acentos (diacríticos combinantes)
    .toLowerCase()
    .trim();
}

/** Auto-sugere o mapeamento a partir dos cabeçalhos detectados. */
export function suggestMapping(headers: string[]): ImportFieldHints {
  const suggestion: ImportFieldHints = {};
  (Object.keys(FIELD_HINTS) as (keyof ImportFieldHints)[]).forEach((fieldKey) => {
    const hints = FIELD_HINTS[fieldKey];
    const match = headers.find((header) => {
      const norm = normalizeHeader(header);
      return hints.some((hint) => norm.includes(hint));
    });
    if (match !== undefined) suggestion[fieldKey] = match;
  });
  return suggestion;
}
